import { message } from 'antd';
import config from 'config';
import { useEffect, useMemo, useRef, useState } from 'react';

/**
 * Configuration options for the `useRegionFilterInit` hook.
 * @template TFilterInput - The shape of the filter object.
 */
type UseRegionFilterInitOptions<TFilterInput> = {
  /** Interface to interact with the external filter state management. */
  filterHook: {
    setFilterOption: (option: {
      fieldName: keyof TFilterInput;
      value: unknown;
    }) => void;
    getFilterObject: () => TFilterInput;
  };

  /**
   * The specific key within `TFilterInput` that holds the region value.
   */
  filterFieldName: keyof TFilterInput;

  /**
   * Flag indicating if the user has permission to view data from all regions.
   */
  canViewAllRegions: boolean;

  /**
   * Whether the user has any region restrictions.
   */
  hasRegionRestrictions: boolean;

  /**
   * Region names from user's assigned regions.
   */
  assignedRegionNames: string[];

  /**
   * Master switch to enable or disable this hook's logic.
   */
  isEnabled?: boolean;

  /**
   * Optional message to display when the filter is successfully initialized.
   */
  successMessage?: string;
};

/**
 * Return values from the `useRegionFilterInit` hook.
 */
type UseRegionFilterInitResult = {
  /** Indicates if the hook is still in the process of initializing. */
  isLoading: boolean;

  /** Indicates if the regional filter logic was activated and applied. */
  isRegionalFilterApplied: boolean;

  /**
   * Array of location names derived from `assignedRegions`.
   * These are the values used to populate the filter.
   */
  defaultRegionNames: string[];
};

/**
 * A custom hook that automatically initializes a filter field with the user's assigned regions.
 *
 * This hook is designed for users who have restricted access to specific regions
 * (when `!canViewAllRegions` and `hasRegionRestrictions` are true).
 *
 * The hook performs the following logic:
 * 1. Checks if regional filtration is required based on user permissions and configuration.
 * 2. Verifies if the target filter field (`filterFieldName`) is currently empty.
 * 3. If empty, it populates the filter using `assignedRegionNames`:
 *    - If there is exactly one assigned region, it sets the value as a single string.
 *    - If there are multiple assigned regions, it sets the value as an array of strings.
 * 4. Optionally displays a one-time success message.
 *
 * @template TFilterInput - The generic type representing the structure of the filter object.
 * @param {UseRegionFilterInitOptions<TFilterInput>} options - Configuration options.
 * @returns {UseRegionFilterInitResult} State of the regional filter initialization.
 *
 * @example
 * const { isLoading, isRegionalFilterApplied } = useRegionFilterInit({
 *   filterHook: { setFilterOption, getFilterObject },
 *   filterFieldName: 'region',
 *   assignedRegionNames: user.assignedRegions, // e.g. ['North America'] or ['North', 'South']
 *   canViewAllRegions: user.isAdmin,
 *   hasRegionRestrictions: true,
 *   successMessage: 'Region filter applied automatically'
 * });
 */
export const useRegionFilterInit = <
  TFilterInput extends Record<string, unknown>
>({
  filterHook,
  filterFieldName,
  canViewAllRegions,
  hasRegionRestrictions,
  assignedRegionNames,
  isEnabled = config.DEFAULTS.REGION_FILTRATION_ENABLED,
  successMessage,
}: UseRegionFilterInitOptions<TFilterInput>): UseRegionFilterInitResult => {
  const [isInitialized, setIsInitialized] = useState(false);
  const hasShownMessage = useRef(false);

  // Determine if we need to enforce the regional filter logic
  const needsRegionalFilter =
    isEnabled && !canViewAllRegions && hasRegionRestrictions;

  useEffect(() => {
    // Prevent re-running if already initialized
    if (isInitialized) return;

    if (!needsRegionalFilter) {
      setIsInitialized(true);
      return;
    }

    if (assignedRegionNames.length === 0) {
      setIsInitialized(true);
      return;
    }

    const currentFilter = filterHook.getFilterObject();
    const currentValue = currentFilter[filterFieldName];

    // Check if the current filter value is empty (string or array)
    const isFilterEmpty =
      !currentValue ||
      (typeof currentValue === 'string' && currentValue.length === 0) ||
      (Array.isArray(currentValue) && currentValue.length === 0);

    if (isFilterEmpty) {
      // Apply the region name to the filter
      const filterValue =
        assignedRegionNames.length === 1
          ? assignedRegionNames[0]
          : assignedRegionNames;

      filterHook.setFilterOption({
        fieldName: filterFieldName,
        value: filterValue,
      });

      // Show success message once
      if (successMessage && !hasShownMessage.current) {
        message.info(successMessage);
        hasShownMessage.current = true;
      }
    }

    setIsInitialized(true);
  }, [
    needsRegionalFilter,
    isInitialized,
    assignedRegionNames,
    filterHook,
    filterFieldName,
    successMessage,
  ]);

  const isLoading = !isInitialized;

  const isRegionalFilterApplied = useMemo(() => {
    if (!needsRegionalFilter) return false;
    if (assignedRegionNames.length === 0) return false;

    const currentFilter = filterHook.getFilterObject();
    const currentValue = currentFilter[filterFieldName];

    if (!currentValue) {
      return false;
    }

    if (typeof currentValue === 'string') {
      return assignedRegionNames.includes(currentValue);
    }

    if (Array.isArray(currentValue)) {
      return assignedRegionNames.every((name) =>
        (currentValue as string[]).includes(name)
      );
    }

    return false;
  }, [filterHook, filterFieldName, assignedRegionNames, needsRegionalFilter]);

  return {
    isLoading,
    isRegionalFilterApplied,
    defaultRegionNames: assignedRegionNames,
  };
};
